/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, Fragment, type ReactNode } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	root: {
		display: 'flex',
		boxSizing: 'border-box',
		width: '100%',
		alignItems: 'flex-start',
		backgroundColor: 'transparent',
		color: 'inherit',
		fontStyle: 'normal',
		fontWeight: token('font.weight.regular'),
		marginBlockEnd: token('space.0'),
		marginBlockStart: token('space.0'),
		marginInlineEnd: token('space.0'),
		marginInlineStart: token('space.0'),
		outline: 'none',
		paddingBlockEnd: token('space.0'),
		paddingBlockStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
		textAlign: 'left',
		textDecoration: 'none',
	},
	avatarItem: {
		minWidth: 0,
		maxWidth: '100%',
		flex: '1 1 100%',
		// This is the padding between the avatar and the text, which is coming from the Figma design.
		paddingInlineStart: token('space.075'),
	},
	baseText: {
		display: 'block',
		color: token('color.text'),
	},
	truncation: {
		overflowX: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

export interface AvatarItemProps {
	/**
	 * Slot to place an avatar element. Use @atlaskit/avatar.
	 */
	avatar: ReactNode;
	/**
	 * PrimaryText text.
	 */
	primaryText?: ReactNode;
}

/**
 * __Avatar item__
 *
 * An avatar item is a wrapper that goes around an avatar when it's displayed alongside text, such as a name or status.
 * This is a variant without default padding/spacing for use in the audit log side panel.
 *
 * - [Examples](https://atlassian.design/components/avatar/avatar-item/examples)
 * - [Code](https://atlassian.design/components/avatar/avatar-item/code)
 */
const AvatarItem: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<AvatarItemProps> & React.RefAttributes<HTMLElement>
> = forwardRef<HTMLElement, AvatarItemProps>(({ avatar, primaryText }, ref) => {
	return (
		<span css={styles.root} ref={ref as React.Ref<HTMLSpanElement>}>
			{avatar}
			<div css={styles.avatarItem}>
				{fg('platform.design-system-team.avatar-item-font-size_830x6') ? (
					<Fragment>
						<Text maxLines={1}>{primaryText}</Text>
					</Fragment>
				) : (
					<Fragment>
						<span css={[styles.baseText, styles.truncation]}>{primaryText}</span>
					</Fragment>
				)}
			</div>
		</span>
	);
});

AvatarItem.displayName = 'AvatarItem';

export default AvatarItem;
